import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { debounce } from "@ember/runloop";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { eventDetailUrl } from "../lib/dec-event-url";
import {
  buildHoursFields,
  publicationLabel as resourcePublicationLabel,
  serializeHoursFields,
  updateHoursField,
} from "../lib/dec-hours";
import { uploadFromResource, uploadImageFile, uploadPreviewUrl } from "../lib/dec-image-upload";
import { safeHttpUrl } from "../lib/dec-safe-url";

function extractErrorMessage(error) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  const errors = responseJSON?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(" ");
  }

  if (typeof errors === "string") {
    return errors;
  }

  return (
    responseJSON?.error ||
    responseJSON?.message ||
    "Venue could not be saved. Check the details and try again."
  );
}

function pad(value) {
  return `${value}`.padStart(2, "0");
}

// See safeDateFormat in discourse-events-calendar.js — kept identical
// across controllers.
function safeDateFormat(date, options, fallback) {
  if (!date) {
    return fallback;
  }
  try {
    const dateObject = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObject.getTime())) {
      return fallback;
    }
    return new Intl.DateTimeFormat(undefined, options).format(dateObject);
  } catch {
    return fallback;
  }
}

function fallbackDate(date) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function fallbackTime(date) {
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function timezoneOptions(selectedTimezone, blankLabel) {
  const selected = `${selectedTimezone || ""}`.trim();
  const supportedZones =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = [
    selected,
    browserTimeZone(),
    "UTC",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Australia/Sydney",
    ...supportedZones,
  ].filter(Boolean);

  return [
    { value: "", label: blankLabel, isSelected: !selected },
    ...[...new Set(zones)].map((zone) => ({
      value: zone,
      label: zone.replace(/_/g, " "),
      isSelected: zone === selected,
    })),
  ];
}

function eventDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return safeDateFormat(
    date,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    fallbackDate(date)
  );
}

function eventTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return safeDateFormat(
    date,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    fallbackTime(date)
  );
}

function sameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function eventDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${eventDate(value)} at ${eventTime(value)}`;
}

function eventTimeRange(startsAtValue, endsAtValue, allDay = false) {
  if (allDay) {
    return "All day";
  }

  if (!startsAtValue) {
    return "";
  }

  if (!endsAtValue) {
    return eventTime(startsAtValue);
  }

  const startsAt = new Date(startsAtValue);
  const endsAt = new Date(endsAtValue);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return "";
  }

  if (sameLocalDate(startsAt, endsAt)) {
    return `${eventTime(startsAtValue)} - ${eventTime(endsAtValue)}`;
  }

  return `${eventDateTime(startsAtValue)} - ${eventDateTime(endsAtValue)}`;
}

function sortEvents(events) {
  return [...events].sort((left, right) => {
    const leftTime = new Date(left.starts_at).getTime() || 0;
    const rightTime = new Date(right.starts_at).getTime() || 0;
    return leftTime - rightTime;
  });
}

function roomUrl(room) {
  return room?.id ? `/calendar/rooms/${room.id}` : "";
}

function normalizeRoom(room) {
  return {
    ...room,
    detailUrl: roomUrl(room),
    capacityLabel: room?.capacity ? `${room.capacity}` : "",
  };
}

function normalizeEvent(event) {
  const room = event.room ? normalizeRoom(event.room) : null;

  return {
    ...event,
    room,
    detailUrl: eventDetailUrl(event),
    displayDate: eventDate(event.starts_at),
    displayTimeRange: eventTimeRange(event.starts_at, event.ends_at, event.all_day),
  };
}

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

function humanize(value) {
  return `${value || ""}`
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizePlaceOption(place) {
  if (!place?.id || !place?.name) {
    return null;
  }

  const address = place.address || place.formatted_address || "";
  return {
    ...place,
    id: place.id,
    name: place.name,
    address,
  };
}

export default class DiscourseEventsCalendarLocationController extends Controller {
  @service siteSettings;

  @tracked location = null;
  @tracked placesEnabled = false;
  @tracked placeMap = null;
  @tracked placeMapElement = null;
  @tracked loadError = "";
  @tracked isEditing = false;
  @tracked isSavingLocation = false;
  @tracked editError = "";
  @tracked editNotice = "";
  @tracked editName = "";
  @tracked editAddress = "";
  @tracked editWebsite = "";
  @tracked editImageUpload = null;
  @tracked editImageUploadId = "";
  @tracked editImageUploadError = "";
  @tracked isUploadingEditImage = false;
  @tracked editLatitude = "";
  @tracked editLongitude = "";
  @tracked editPlaceQuery = "";
  @tracked editPlaceId = "";
  @tracked editPlaceOptions = [];
  @tracked isLoadingEditPlaces = false;
  @tracked editDescription = "";
  @tracked editPublicationStatus = "published";
  @tracked editHoursTimezone = "";
  @tracked editHoursFields = buildHoursFields();
  editPlaceRequestId = 0;

  syncLocationFromModel(model) {
    this.location = model?.location || null;
    this.placesEnabled = model?.placesEnabled === true;
    this.placeMap = null;
    this.placeMapElement = null;
    this.loadError = model?.loadError || "";
    this.isEditing = false;
    this.isSavingLocation = false;
    this.editError = "";
    this.editNotice = "";
    this.resetEditFields();
  }

  get hasLocation() {
    return Boolean(this.location);
  }

  get canManageLocation() {
    return this.location?.can_manage === true && this.location?.persisted === true;
  }

  get publicationLabel() {
    return resourcePublicationLabel(this.location?.publication_status);
  }

  get isDraft() {
    return this.location?.publication_status === "draft";
  }

  get isPublishedPublicationStatus() {
    return this.editPublicationStatus === "published";
  }

  get isDraftPublicationStatus() {
    return this.editPublicationStatus === "draft";
  }

  get hasHours() {
    return Boolean(this.location?.hours_summary && this.location.hours_summary !== "No hours set");
  }

  get editHoursTimezoneOptions() {
    return timezoneOptions(this.editHoursTimezone, "Use calendar default");
  }

  get createEventUrl() {
    const locationId = this.location?.record_id || this.location?.id;
    if (!locationId) {
      return "/calendar?create=1";
    }

    return `/calendar?${new URLSearchParams({ create: "1", location_id: `${locationId}` }).toString()}`;
  }

  get feedUrl() {
    const locationId = this.location?.id;
    return locationId ? `/calendar/locations/${encodeURIComponent(locationId)}/feed.ics` : "/calendar/feed.ics";
  }

  get canSaveLocation() {
    return this.canManageLocation && this.editName.trim().length > 0 && !this.isSavingLocation;
  }

  get cannotSaveLocation() {
    return !this.canSaveLocation;
  }

  get rooms() {
    return (this.location?.rooms || []).map(normalizeRoom);
  }

  get hasRooms() {
    return this.rooms.length > 0;
  }

  get hasDescription() {
    return Boolean(this.location?.description);
  }

  get hasLocationImage() {
    return Boolean(this.location?.image_url);
  }

  get editImagePreviewUrl() {
    return uploadPreviewUrl(this.editImageUpload);
  }

  get hasEditImageUpload() {
    return Boolean(this.editImagePreviewUrl);
  }

  get locationImageAlt() {
    return this.location?.name ? `Image for ${this.location.name}` : "Venue image";
  }

  get hasAddress() {
    return Boolean(this.location?.address);
  }

  get hasStandaloneAddress() {
    return this.hasAddress && !this.hasPlace;
  }

  get hasWebsite() {
    return Boolean(this.websiteUrl);
  }

  get websiteUrl() {
    return safeHttpUrl(this.location?.website);
  }

  get placeUrl() {
    const slugOrId = this.location?.place?.slug || this.location?.place?.id;
    return slugOrId ? `/places/${slugOrId}` : "";
  }

  get hasPlace() {
    return Boolean(this.location?.place?.name);
  }

  get hasPlaceUrl() {
    return Boolean(this.placeUrl);
  }

  get hasEditPlaceOptions() {
    return this.editPlaceOptions.length > 0;
  }

  get placeAddress() {
    return this.location?.place?.address || this.location?.place?.formatted_address || "";
  }

  get placeTypeLabel() {
    return this.location?.place?.place_type?.label || humanize(this.location?.place?.kind);
  }

  get hasPlaceTypeLabel() {
    return Boolean(this.placeTypeLabel);
  }

  get placeThumbnailUrl() {
    return this.location?.place?.cover_image_url || "";
  }

  get hasPlaceThumbnail() {
    return Boolean(this.placeThumbnailUrl);
  }

  get placeWebsite() {
    return safeHttpUrl(this.location?.place?.website);
  }

  get hasPlaceWebsite() {
    return Boolean(this.placeWebsite);
  }

  get placeMapMarkers() {
    const place = this.location?.place;
    const latitude = parseFloat(place?.latitude);
    const longitude = parseFloat(place?.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return [];
    }

    return [
      {
        id: place.id,
        slug: place.slug,
        label: place.name,
        latitude,
        longitude,
      },
    ];
  }

  get hasPlaceMap() {
    return this.placesEnabled && this.placeMapMarkers.length > 0;
  }

  get hasCoordinates() {
    return present(this.location?.latitude) && present(this.location?.longitude);
  }

  get coordinatesLabel() {
    if (!this.hasCoordinates) {
      return "";
    }

    return `${this.location.latitude}, ${this.location.longitude}`;
  }

  get upcomingEvents() {
    return sortEvents(this.location?.upcoming_events || []).map(normalizeEvent);
  }

  get hasUpcomingEvents() {
    return this.upcomingEvents.length > 0;
  }

  resetEditFields() {
    this.editName = this.location?.name || "";
    this.editAddress = this.location?.address || "";
    this.editWebsite = this.location?.website || "";
    this.editImageUpload = uploadFromResource(this.location) || null;
    this.editImageUploadId = this.location?.image_upload_id ? `${this.location.image_upload_id}` : "";
    this.editImageUploadError = "";
    this.isUploadingEditImage = false;
    this.editLatitude = present(this.location?.latitude) ? `${this.location.latitude}` : "";
    this.editLongitude = present(this.location?.longitude) ? `${this.location.longitude}` : "";
    this.editPlaceQuery = this.location?.place?.name || "";
    this.editPlaceId = this.location?.place_id ? `${this.location.place_id}` : "";
    this.editPlaceOptions = [];
    this.isLoadingEditPlaces = false;
    this.editDescription = this.location?.description || "";
    this.editPublicationStatus = this.location?.publication_status || "published";
    this.editHoursTimezone = this.location?.hours_timezone || "";
    this.editHoursFields = buildHoursFields(this.location?.hours_of_operation || {});
  }

  @action
  startEditing() {
    this.resetEditFields();
    this.editError = "";
    this.editNotice = "";
    this.isEditing = true;
  }

  @action
  cancelEditing() {
    this.isEditing = false;
    this.editError = "";
    this.resetEditFields();
  }

  @action
  updateDescription(event) {
    this.editDescription = event.target.value;
  }

  @action
  async uploadEditImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.editImageUploadError = "";
    this.isUploadingEditImage = true;

    try {
      const upload = await uploadImageFile(file);
      this.editImageUpload = upload;
      this.editImageUploadId = upload?.id ? `${upload.id}` : "";
    } catch (error) {
      this.editImageUploadError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isUploadingEditImage = false;
    }
  }

  @action
  removeEditImage() {
    this.editImageUpload = null;
    this.editImageUploadId = "";
    this.editImageUploadError = "";
  }

  @action
  async renderPlaceMap(element) {
    this.placeMapElement = element;
    this.placeMap?.remove?.();
    this.placeMap = null;

    if (!this.hasPlaceMap || typeof window === "undefined") {
      return;
    }

    const requireModule = window.requirejs || window.require;
    if (typeof requireModule !== "function") {
      return;
    }

    let mapModule;
    try {
      mapModule = requireModule("discourse/plugins/discourse-places/discourse/lib/place-map");
    } catch {
      return;
    }

    if (typeof mapModule?.renderPlaceMap !== "function") {
      return;
    }

    let map;
    try {
      map = await mapModule.renderPlaceMap(element, {
        markers: this.placeMapMarkers,
        provider: this.siteSettings?.discourse_places_map_provider,
        maptilerApiKey: this.siteSettings?.discourse_places_maptiler_api_key,
        maptilerStyle: this.siteSettings?.discourse_places_maptiler_style,
        openFreeMapStyle: this.siteSettings?.discourse_places_openfreemap_style,
      });
    } catch {
      return;
    }

    if (this.placeMapElement === element) {
      this.placeMap = map;
    } else {
      map?.remove?.();
    }
  }

  @action
  destroyPlaceMap() {
    this.placeMap?.remove?.();
    this.placeMap = null;
    this.placeMapElement = null;
  }

  @action
  updatePublicationStatus(event) {
    this.editPublicationStatus = event.target.value || "published";
  }

  @action
  updateHoursTimezone(event) {
    this.editHoursTimezone = event.target.value;
  }

  @action
  updateEditPlaceQuery(event) {
    this.editPlaceQuery = event.target.value;
    this.editPlaceId = "";
    debounce(this, this.loadEditPlaceOptions, this.editPlaceQuery, 250);
  }

  async loadEditPlaceOptions(query) {
    const value = `${query || ""}`.trim();

    if (!this.placesEnabled || value.length < 2) {
      this.editPlaceOptions = [];
      this.isLoadingEditPlaces = false;
      return;
    }

    const requestId = ++this.editPlaceRequestId;
    this.isLoadingEditPlaces = true;

    try {
      const response = await ajax("/places/autocomplete.json", {
        data: { q: value, limit: 8 },
      });

      if (requestId !== this.editPlaceRequestId) {
        return;
      }

      this.editPlaceOptions = (Array.isArray(response?.places) ? response.places : [])
        .map(normalizePlaceOption)
        .filter(Boolean);
    } catch {
      if (requestId === this.editPlaceRequestId) {
        this.editPlaceOptions = [];
      }
    } finally {
      if (requestId === this.editPlaceRequestId) {
        this.isLoadingEditPlaces = false;
      }
    }
  }

  @action
  selectEditPlace(place, event) {
    event?.preventDefault();
    const selectedPlace = normalizePlaceOption(place);
    if (!selectedPlace) {
      return;
    }

    this.editPlaceId = `${selectedPlace.id}`;
    this.editPlaceQuery = selectedPlace.name;
    this.editPlaceOptions = [];

    if (!this.editAddress && selectedPlace.address) {
      this.editAddress = selectedPlace.address;
    }
  }

  @action
  clearEditPlace(event) {
    event?.preventDefault();
    this.editPlaceQuery = "";
    this.editPlaceId = "";
    this.editPlaceOptions = [];
    this.isLoadingEditPlaces = false;
  }

  @action
  toggleHoursDay(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      enabled: event.target.checked,
    });
  }

  @action
  updateHoursStart(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      start: event.target.value,
    });
  }

  @action
  updateHoursEnd(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      end: event.target.value,
    });
  }

  @action
  async saveLocation(event) {
    event?.preventDefault();

    if (!this.canSaveLocation) {
      return;
    }

    this.isSavingLocation = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(
        `/calendar/locations/${encodeURIComponent(this.location.id)}.json`,
        {
          type: "PUT",
          data: {
            location: {
              name: this.editName,
              address: this.editAddress,
              website: this.editWebsite,
              image_upload_id: this.editImageUploadId,
              place_id: this.editPlaceId,
              description: this.editDescription,
              publication_status: this.editPublicationStatus,
              hours_timezone: this.editHoursTimezone,
              hours_of_operation: serializeHoursFields(this.editHoursFields),
            },
          },
        }
      );

      this.location = response?.location || response || this.location;
      this.isEditing = false;
      this.editNotice = "Location updated.";
      this.resetEditFields();
    } catch (error) {
      this.editError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isSavingLocation = false;
    }
  }
}
